import { getHandicapDisplay, isGameStandard, sortByGroupName } from "./../index";
import { Game, GState } from "../../types/game";
import Moment from "moment";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import { pdfToImage } from "../pdfUtils";
import { isCombinedGame } from "..";
import { Gender } from "../../types/user";
import { PayStatus } from "../../types/player";

let doc: jsPDF | null = null;
const leftPading = 5;
const rightPading = 30;
let lineIndex = 0;
const nextLine = 5;

const writeKeyValueText = (key: string, value: string) => {
  doc?.text(key, leftPading, lineIndex);
  doc?.text(value, rightPading, lineIndex);
  lineIndex += nextLine;
};

const getData = (game: Game, scoringType: string) => {
  const result: any = [];
  const isCombined = isCombinedGame(game.competition);
  const players = game.players ? Object.values(game.players).filter(x => x.isTeamCard || !isCombined) : [];

  players.forEach((player: any) => {
    let data: any = [
      player.name,
      player.lastName ? player.lastName : "",
      getHandicapDisplay(player.hcp),
      player.hci ? Number(player.hci).toFixed(1) : 0,
      player.cdh ? player.cdh : "",
    ];
    let startIndex = 0;
    let endingIndex: number = game.course.holesNumber;

    if (scoringType === "front") endingIndex = game.course.holesNumber / 2;
    else if (scoringType === "back") startIndex = game.course.holesNumber / 2;

    if (player.holes) {
      Object.values(player.holes)
        .slice(startIndex, endingIndex)
        .forEach((hole: any) => {
          data = [...data, hole?.gross || hole];
        });
      result.push(data);
    }
  });
  return result;
};

const createHeader = (game: Game, scoringType: string) => {
  const header = ["First Name", "Last Name", "PH", "HI", "CDH No."];
  let startIndex = 0;
  let endingIndex: number = game.course.holesNumber;

  if (scoringType === "front") endingIndex = game.course.holesNumber / 2;
  else if (scoringType === "back") startIndex = game.course.holesNumber / 2;

  for (let i = startIndex; i < endingIndex; i++) {
    header.push(`Hole\n${i + 1}`);
  }
  return header;
};

const writeGameDetails = (game: Game) => {
  writeKeyValueText("Golf Club:", game.course.club.name);
  writeKeyValueText(`Golf Course:`, game.course.name);
  writeKeyValueText(`Date:`, Moment(game.date).format("DD/MM/YYYY"));
  writeKeyValueText(`Golf Name:`, game.name);
  writeKeyValueText(`Golf Code:`, game.code);

  Object.values(game.scoreCards).forEach(scoreCard => {
    writeKeyValueText(
      `Tee (${scoreCard.gender === Gender.Men ? "Men" : "Ladies"}):`,
      `${scoreCard.teeColor}, CR:${scoreCard.courseRating}, Slope: ${scoreCard.slope
      }, Par: ${scoreCard.holesParams.reduce((total: number, hole) => total + hole.par, 0)}`,
    );
  });
};

const writeLeaderboardDetails = (game: Game) => {
  writeKeyValueText("Tournament:", game.name);
  writeKeyValueText(`Date:`, Moment(game.date).format("DD/MM/YYYY"));
};

const setPageStyle = () => {
  lineIndex = 10;
  doc?.setFontSize(10);
};

const getResultJSPDFObject = (game: Game, scoringType: string, isTournament: boolean) => {
  doc = new jsPDF("l", "mm", "a4");
  setPageStyle();
  isTournament ? writeLeaderboardDetails(game) : writeGameDetails(game);
  autoTable(doc, {
    head: [createHeader(game, scoringType)],
    body: getData(game, scoringType),
    startY: lineIndex,
    margin: { horizontal: 0, vertical: 0 },
    bodyStyles: {
      cellWidth: "auto",
      fontSize: 9,
    },
    styles: {
      halign: "center",
      valign: "middle",
    },
  });
  return doc;
};

const getLeaderboardJSPDFObject = (tableTitle: string, game: Game) => {
  doc = new jsPDF("p", "mm", "a4");
  setPageStyle();
  writeGameDetails(game);
  doc?.text(game.state === GState.Done ? "FINAL RESULT" : "", 100, 40);

  // For leaderboards, prefer DOM capture as it handles all the complex columns correctly
  // (Position, Player, PH, Gross, Net, Pts, New PH, etc. depending on game type)
  const tableElement = document.querySelector(`#${tableTitle}`);
  
  if (tableElement) {
    console.log("[PDF] Generating leaderboard from DOM (captures all columns correctly)");
    autoTable(doc, { 
      html: `#${tableTitle}`, 
      startY: 50,
      headStyles: {
        fillColor: [66, 165, 245], // Material-UI blue[400] / primary.light - matches table headers
        textColor: 255,
        fontStyle: "bold",
      },
    });
  } else {
    // Fallback: data-driven generation with basic columns
    console.log("[PDF] DOM element not found, using data-driven fallback");
    const isCombined = isCombinedGame(game.competition);
    const players = Object.values(game.players || {})
      .filter((x: any) => x.isTeamCard || !isCombined)
      .map((player: any) => {
        const totalScore = player.holes
          ? Object.values(player.holes).reduce((sum: number, hole: any) => sum + (hole?.gross || hole || 0), 0)
          : 0;
        return {
          name: `${player.name} ${player.lastName || ""}`.trim(),
          ph: getHandicapDisplay(player.hcp),
          total: totalScore,
        };
      })
      .sort((a: any, b: any) => a.total - b.total);

    if (players.length > 0) {
      autoTable(doc, {
        head: [["Position", "Player", "Playing Handicap", "Total Score"]],
        body: players.map((p: any, index: number) => [index + 1, p.name, p.ph, p.total]),
        startY: 50,
        styles: {
          halign: "center",
          valign: "middle",
          fontSize: 10,
        },
        headStyles: {
          fillColor: [66, 165, 245], // Material-UI blue[400] / primary.light
          textColor: 255,
          fontStyle: "bold",
        },
      });
    } else {
      doc?.text("No player data available", 105, 60, { align: "center" });
    }
  }

  return doc;
};

const getTeamName = (players: any[], payBeforePlay: boolean = false) => {
  const groups = players.reduce((acc: any, p: any) => {
    const teamId = p.team?._id || p.team;
    if (!acc[teamId]) acc[teamId] = [];
    acc[teamId].push(p);
    return acc;
  }, {});

  return Object.values(groups)
    .map((ps: any) =>
      ps
        .map((p: any) => p.lastName || p.name)
        .join("/ "),
    )
    .join(" and ");
};

interface StartSheetPDFOptions {
  includeUnpaidPlayers?: boolean;
}

const getStartSheetPlayers = (game: Game, options: StartSheetPDFOptions = {}) => {
  const players = Object.values(game.players || {}).filter((p: any) => !p.isTeamCard);
  const isTeamGame = game.competition?.playingRules?.playingAs === "team";
  const payBeforePlay = game.paymentRules?.payBeforePlay;

  if (isTeamGame || !payBeforePlay || options.includeUnpaidPlayers) return players;

  return players.filter((p: any) => p.payStatus !== PayStatus.NOT_PAID);
};

export const getStartSheetPDFStats = (game: Game, options: StartSheetPDFOptions = {}) => {
  const players = getStartSheetPlayers(game, options);
  const groupedPlayers = players.filter((p: any) => p.group?.name);
  const groupKeys = new Set(groupedPlayers.map((p: any) => `${p.group.name}${p.group.startingHole || 1}`));

  return {
    playerCount: players.length,
    groupedPlayerCount: groupedPlayers.length,
    groupCount: groupKeys.size,
  };
};

const customStartSheetHandicap = (player: any, competitionId: string): string => {
  const fromMap = competitionId && player.playingHcps ? player.playingHcps[competitionId] : undefined;
  if (fromMap != null && fromMap !== "") return getHandicapDisplay(fromMap);
  if (player.hcp != null && player.hcp !== "") return getHandicapDisplay(player.hcp);
  return getHandicapDisplay(player.hci);
};

// data-driven Start Sheet generator
export const getStartSheetJSPDFObject = (game: Game, options: StartSheetPDFOptions = {}) => {
  doc = new jsPDF("p", "mm", "a4");
  setPageStyle();
  writeGameDetails(game);

  const players = getStartSheetPlayers(game, options);
  const groups = Object.values(
    players.reduce((acc: any, p: any) => {
      const groupKey = p.group ? `${p.group.name}${p.group.startingHole || 1}` : "Unassigned";
      if (!acc[groupKey]) acc[groupKey] = [];
      acc[groupKey].push(p);
      return acc;
    }, {}),
  ).sort((a: any, b: any) => {
    const holeA = a[0]?.group?.startingHole || 1;
    const holeB = b[0]?.group?.startingHole || 1;
    if (holeA !== holeB) return holeA - holeB;
    return sortByGroupName(a[0]?.group?.name || "", b[0]?.group?.name || "");
  });

  const isTeamGame = game.competition?.playingRules?.playingAs === "team";
  const payBeforePlay = game.paymentRules?.payBeforePlay;
  const isCustomGame = !isGameStandard(game.competition?.type);

  const body = groups.map((groupPlayers: any) => {
    const group = groupPlayers[0]?.group;
    const rowData = [group?.name || "N/A", group?.startingHole || 1];

    if (isTeamGame) {
      const teamName = getTeamName(groupPlayers, payBeforePlay);
      rowData.push(teamName);
    } else {
      groupPlayers.forEach((player: any) => {
        const playerName = `${player.name || ""} ${player.lastName || ""}`.trim();
        const hci = isCustomGame
          ? customStartSheetHandicap(player, game.competition?._id != null ? String(game.competition._id) : "")
          : getHandicapDisplay(player.hci);
        const displayHci = player.shcp ? ` (${hci} ${player.shcp})` : hci ? ` (${hci})` : "";
        rowData.push(`${playerName}${displayHci}`);
      });
    }
    return rowData;
  });

  if (body.length > 0) {
    const maxPlayersPerGroup = Math.max(...body.map(row => row.length - 2));
    const columnWidth = Math.min((200 - 30 - 20) / maxPlayersPerGroup, 40);

    autoTable(doc, {
      head: [["Group", "Tee", ...Array(maxPlayersPerGroup).fill("Players")]],
      body: body,
      startY: lineIndex + 10,
      styles: {
        fontSize: 10,
        cellPadding: 3,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [66, 165, 245],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 20 },
      },
    });
  } else {
    doc?.text("No group assignments found", 105, 60, { align: "center" });
  }

  return doc;
};

export const exportStartSheetToBase64 = (game: Game, options?: StartSheetPDFOptions) => {
  try {
    const stats = getStartSheetPDFStats(game, options);
    if (stats.playerCount === 0 || stats.groupedPlayerCount === 0 || stats.groupCount === 0) {
      console.error("[PDF] Start Sheet PDF generation blocked - no grouped players found", stats);
      return "";
    }

    const result = getStartSheetJSPDFObject(game, options).output("datauristring");
    console.log(`[PDF] Start Sheet Data-Driven PDF generated, size: ${result?.length || 0} chars`);
    return result;
  } catch (error) {
    console.error("[PDF] Error generating start sheet PDF:", error);
    return "";
  }
};

export const exportStartSheetEmailToBase64 = (game: Game) =>
  exportStartSheetToBase64(game, { includeUnpaidPlayers: true });

export const exportResultToPDF = (game: Game, scoringType: string, isTournament = false) => {
  getResultJSPDFObject(game, scoringType, isTournament).save(`${game.name}-${game.code}.pdf`);
};

export const exportLeaderboardToPDF = (tableTitle: string, game: Game) => {
  getLeaderboardJSPDFObject(tableTitle, game).save(`leaderboard-${game.name}-${game.code}.pdf`);
};

export const exportLeaderboardToBase64 = (tableTitle: string, game: Game) => {
  console.log(`[PDF] exportLeaderboardToBase64 called for game: ${game?.name}, tableTitle: ${tableTitle}`);

  if (!game) {
    console.error("[PDF] Game object is null/undefined");
    return "";
  }

  if (!game.players || Object.keys(game.players).length === 0) {
    console.error("[PDF] No players found in game data");
    return "";
  }

  try {
    const result = getLeaderboardJSPDFObject(tableTitle, game).output("datauristring");
    console.log(`[PDF] Leaderboard PDF generated successfully, size: ${result?.length || 0} chars`);
    return result;
  } catch (error) {
    console.error("[PDF] Error generating leaderboard PDF:", error);
    return "";
  }
};

export const exportLeaderboardToImageFile = async (
  filename: string,
  tableTitle: string,
  game: Game,
): Promise<File[] | null> => {
  const blob = getLeaderboardJSPDFObject(tableTitle, game).output("arraybuffer", filename);
  const images = await pdfToImage(blob);
  if (!images) return null;
  return images.map(image => new File([image], `${filename}.jpg`, { type: "image/jpeg" }));
};

export const exportResultToImageFile = async (
  filename: string,
  game: Game,
  scoringType: string,
  isTournament = false,
): Promise<File[] | null> => {
  const blob = getResultJSPDFObject(game, scoringType, isTournament).output("arraybuffer", filename);
  const images = await pdfToImage(blob);
  if (!images) return null;
  return images.map(image => new File([image], `${filename}.jpg`, { type: "image/jpeg" }));
};
